import { Lock } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import type { ButtonClickResponse } from '@/types/api/record'
import { cn } from '@/utils/cn'
import { getRelativeTime } from '@/utils/date'
import { parseApiError } from '@/utils/error-parser'

import { buttonOptions, type ButtonColor } from '../utils/buttonField'
import { FIELD_VIEW_ONLY_HINT } from '../utils/fieldAccess'

const VARIANT: Record<ButtonColor, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  primary: 'default',
  success: 'success',
  warning: 'warning',
  danger: 'destructive',
  neutral: 'secondary',
}

interface RecordButtonFieldProps {
  field: { key: string; label: string; options: Record<string, unknown> | null }
  /** The latest click on this record, from the record detail. */
  lastClick?: { at: string; by: string } | null
  /** Record edit permission and edit access to this field. */
  canClick: boolean
  /** The user may edit the record but this field's access is view-only. */
  viewOnly?: boolean
  /** The page has unsaved edits; automations read saved values, so they would act on the old ones. */
  unsaved?: boolean
  click: (fieldKey: string) => Promise<ButtonClickResponse>
}

/** A BUTTON field on the record detail page: a button, not an input; it holds no value. */
export function RecordButtonField({ field, lastClick, canClick, viewOnly = false, unsaved = false, click }: RecordButtonFieldProps) {
  const [pending, setPending] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const button = buttonOptions(field)
  const text = field.label
  const used = button.oneShot && !!lastClick

  const run = async () => {
    setPending(true)
    try {
      const { automationsRun } = await click(field.key)
      toast.success(automationsRun === 0
        ? 'No automations are set up for this button'
        : `${text} ran ${automationsRun} automation${automationsRun === 1 ? '' : 's'}`)
    } catch (error) {
      toast.error(parseApiError(error).detail || `Could not run ${text}`)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="rounded-xl border border-neutral-100 bg-neutral-50/40 px-3 pt-2.5 pb-3">
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant={VARIANT[button.buttonColor]}
          // Disabled while the request runs: a second click would start every automation again
          disabled={!canClick || unsaved || pending || used}
          onClick={() => (button.confirm ? setConfirming(true) : void run())}
        >
          {text}
        </Button>
        {viewOnly && (
          <span title={FIELD_VIEW_ONLY_HINT} aria-label={FIELD_VIEW_ONLY_HINT} className="text-neutral-400">
            <Lock className="h-3 w-3" />
          </span>
        )}
      </div>
      {canClick && unsaved && (
        <p className="mt-1.5 text-[10px] text-amber-600">Save your changes first</p>
      )}
      {lastClick && (
        <p className={cn('mt-1.5 text-[10px] text-neutral-400')}>
          {used ? 'Used' : 'Last clicked'} by {lastClick.by} · {getRelativeTime(lastClick.at)}
        </p>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`${text}?`}
        description={button.confirmMessage || 'This will run the automations set up for this button.'}
        confirmLabel={text}
        destructive={button.buttonColor === 'danger'}
        // ConfirmDialog leaves closing to the caller
        onConfirm={() => { setConfirming(false); void run() }}
      />
    </div>
  )
}
